'use client'

import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'
import { IRestaurantDeal } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { Gradient } from '@/components/gradient'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { Locale, toLocalePriceString } from '@/setup/locales'
import { noOp } from '@/utils/functions/noOp'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'
import { rgba } from '@/utils/styles/rgba'

interface IRestaurantDealCardProps {
    deal: IRestaurantDeal
}

export const RestaurantDealCard = ({ deal }: IRestaurantDealCardProps) => {
    const locale = useLocale()
    const { restaurant, restaurantSlug, citySlug } = useRestaurantDetailContext()

    const t = useTranslations()

    const link = useMemo(() => {
        if (!restaurant) return

        return `https://app.adjust.com/147kxpz8?campaign=RestaurantDetail&adgroup=${restaurantSlug}-${citySlug}&engagement_type=fallback_click`
    }, [restaurant, restaurantSlug, citySlug])

    const readableLocationCondition = useMemo(() => {
        switch (deal.locationCondition) {
            case 'onSite':
                return t('restaurants.detail.deals.locationConditions.onSite')
        }
    }, [deal.locationCondition, t])

    const formattedDealValue = useMemo(() => {
        if (!deal.value) return

        return toLocalePriceString({
            price: deal.value ?? 0,
            locale: locale as Locale,
            currencySymbol: restaurant?.city?.country?.currency?.symbol ?? '€',
            rounded: true,
        })
    }, [deal.value, locale, restaurant])

    const isEventDeal = Boolean(deal.eventDeal)

    return (
        <div
            className={cn(
                `rounded-[20px] py-4 w-full max-w-full md:max-w-[547px] shadow-soft justify-between
                min-h-full flex flex-col`,
                isEventDeal ? 'bg-neotaste-dark text-neotaste' : 'bg-neotaste text-black'
            )}
        >
            <div className="w-full overflow-hidden">
                <h3 className="text-[20px] md:text-[24px] font-bold mb-3.5 px-5 flex items-center gap-2">
                    {isEventDeal && (
                        <img
                            src="/_images/big-icons/bolt.svg"
                            alt="Bold icon"
                            aria-hidden
                            className="h-[1em] w-[1em]"
                        />
                    )}
                    {deal.name}
                </h3>

                <div className="relative">
                    <div className="flex flex-row gap-1 mb-3.5 px-5 overflow-y-hidden overflow-x-scroll scrollbar-hide relative">
                        {Boolean(formattedDealValue) && (
                            <RestaurantDealCardTag
                                label={t('restaurants.detail.deals.value', {
                                    readableValue: formattedDealValue ?? '',
                                })}
                                borderColor={isEventDeal ? COLORS.neotaste : undefined}
                            />
                        )}

                        <RestaurantDealCardTag
                            label={t('restaurants.detail.deals.days', {
                                days: deal.daysToReset ?? '',
                            })}
                            borderColor={isEventDeal ? COLORS.neotaste : undefined}
                        />

                        {readableLocationCondition && (
                            <RestaurantDealCardTag
                                label={readableLocationCondition}
                                borderColor={isEventDeal ? COLORS.neotaste : undefined}
                            />
                        )}
                    </div>

                    <Gradient
                        className="absolute right-0 top-0 min-w-[20px] min-h-[32px]"
                        direction="to-left"
                        color={isEventDeal ? 'from-neotaste-dark' : 'from-neotaste'}
                    />
                </div>

                <p
                    className={cn(
                        'mb-3.5 md:text-[16px] text-[14px] px-5',
                        isEventDeal ? 'text-neotaste' : 'text-gray-700'
                    )}
                >
                    {deal.conditions}
                </p>
            </div>

            <div className="px-5 w-full">
                <Button
                    {...(link ? { href: { externalUrl: link } } : { onClick: noOp })}
                    styleType="primary"
                    shape="pill"
                    backgroundColor={isEventDeal ? '#53F29333' : rgba(0, 0, 0, 0.1)}
                    contentColor={isEventDeal ? COLORS.neotaste : undefined}
                    borderColor={isEventDeal ? COLORS.neotaste : undefined}
                    height="52px"
                    width="100%"
                >
                    {t('restaurants.detail.deals.cta')}
                </Button>
            </div>
        </div>
    )
}

interface IRestaurantDealCardTagProps {
    icon?: React.ReactNode
    label: string
    borderColor?: string
}

const RestaurantDealCardTag = ({ icon, label, borderColor }: IRestaurantDealCardTagProps) => {
    return (
        <div
            className="rounded-[40px] min-h-[32px] max-h-[32px] flex items-center justify-center w-fit gap-x-1 px-3"
            style={{
                border: `2px solid ${borderColor ?? rgba(0, 0, 0, 0.1)}`,
            }}
        >
            {icon && <span>{icon}</span>}

            <p className="font-bold text-[13px] whitespace-nowrap">{label}</p>
        </div>
    )
}
